import { useQuery } from '@apollo/client/react';
import { Box, Button, Heading, Link, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { CollapsibleProcessorFileRow } from '#components/collapsible-processor-file-row';
import { PageContentRoot } from '#components/page-content-root';
import { PageHeaderPortal } from '#components/page-header-portal';
import { GET_PROCESSOR } from '#graphql/queries/processor-detail';

export function ProcessorFilesPage() {
  const { processorId } = useParams<{ processorId: string }>();

  const { data, loading, error, refetch } = useQuery(GET_PROCESSOR, {
    variables: { id: processorId ?? '' },
    skip: !processorId,
  });

  const detail = data?.processor;

  const headerLink = processorId ? (
    <Link asChild color="blue.600" fontSize="sm" fontWeight="medium">
      <RouterLink to={`/processors/${encodeURIComponent(processorId)}`}>
        Processor
      </RouterLink>
    </Link>
  ) : null;

  if (!processorId) {
    return (
      <PageContentRoot>
        <Text color="red.500">Missing processor id.</Text>
      </PageContentRoot>
    );
  }

  const titleSyncKey = `${processorId}-${loading ? 'l' : ''}${error ? 'e' : ''}${detail ? 'd' : 'n'}`;

  if (loading) {
    return (
      <>
        <PageHeaderPortal
          title={`${processorId} · Files`}
          titleSyncKey={titleSyncKey}
        >
          {headerLink}
        </PageHeaderPortal>
        <PageContentRoot>
          <Text color="fg.muted">Loading…</Text>
        </PageContentRoot>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeaderPortal
          title={`${processorId} · Files`}
          titleSyncKey={titleSyncKey}
        >
          {headerLink}
        </PageHeaderPortal>
        <PageContentRoot>
          <Text color="red.500">Error: {error.message}</Text>
          <Button mt={4} size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </PageContentRoot>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeaderPortal
          title={`${processorId} · Files`}
          titleSyncKey={titleSyncKey}
        >
          {headerLink}
        </PageHeaderPortal>
        <PageContentRoot>
          <Heading size="lg" color="fg" mb={4}>
            Processor not found
          </Heading>
          <Text color="fg.muted" mb={4}>
            No processor with id &quot;{processorId}&quot;.
          </Text>
        </PageContentRoot>
      </>
    );
  }

  return (
    <>
      <PageHeaderPortal
        title={`${processorId} · Files`}
        titleSyncKey={titleSyncKey}
      >
        {headerLink}
      </PageHeaderPortal>
      <PageContentRoot>
        <VStack gap={6} align="stretch">
          <Box>
            <Heading size="md" color="fg" mb={3}>
              Processor files
            </Heading>
            <Text fontSize="sm" color="fg.muted" mb={4}>
              Expand a row to load and view source. Order matches execution
              (prepends first, then main).
            </Text>
            <VStack gap={3} align="stretch">
              {detail.fileRefs.map((ref) => (
                <CollapsibleProcessorFileRow
                  key={ref.id}
                  fileId={ref.id}
                  sortOrder={ref.sortOrder}
                  role={ref.role}
                />
              ))}
            </VStack>
          </Box>
        </VStack>
      </PageContentRoot>
    </>
  );
}
